/**
 * GuestDetails Component
 *
 * Guest count selector with adults, children, and infants.
 */

import React, { useState } from 'react';

interface GuestCount {
  adults: number;
  children: number;
  infants: number;
}

interface GuestDetailsProps {
  guests: GuestCount;
  maxGuests: number;
  onChange: (guests: GuestCount) => void;
}

export const GuestDetails: React.FC<GuestDetailsProps> = ({
  guests,
  maxGuests,
  onChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const totalGuests = guests.adults + guests.children;
  const canAddGuest = totalGuests < maxGuests;

  const updateCount = (type: keyof GuestCount, delta: number) => {
    const newValue = guests[type] + delta;

    // Validate
    if (type === 'adults' && newValue < 1) return; // At least 1 adult
    if (newValue < 0) return;
    if (type !== 'infants' && !canAddGuest && delta > 0) return;

    onChange({ ...guests, [type]: newValue });
  };

  const formatGuestSummary = () => {
    const parts = [];
    if (guests.adults > 0) {
      parts.push(`${guests.adults} adult${guests.adults !== 1 ? 's' : ''}`);
    }
    if (guests.children > 0) {
      parts.push(`${guests.children} child${guests.children !== 1 ? 'ren' : ''}`);
    }
    if (guests.infants > 0) {
      parts.push(`${guests.infants} infant${guests.infants !== 1 ? 's' : ''}`);
    }
    return parts.join(', ');
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Collapsed View */}
      {!isEditing && (
        <button
          onClick={() => setIsEditing(true)}
          className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 mb-1">Guests</div>
              <div className="font-medium">{formatGuestSummary()}</div>
            </div>
            <span className="text-primary-600 text-sm font-medium">Edit</span>
          </div>
        </button>
      )}

      {/* Expanded View */}
      {isEditing && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <span className="font-medium">Guests</span>
            <button
              onClick={() => setIsEditing(false)}
              className="text-primary-600 text-sm font-medium"
            >
              Done
            </button>
          </div>

          {/* Guest Counters */}
          <div className="space-y-4">
            {/* Adults */}
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Adults</div>
                <div className="text-sm text-gray-500">Age 13+</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => updateCount('adults', -1)}
                  disabled={guests.adults <= 1}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MinusIcon className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium">{guests.adults}</span>
                <button
                  onClick={() => updateCount('adults', 1)}
                  disabled={!canAddGuest}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Children */}
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Children</div>
                <div className="text-sm text-gray-500">Ages 2-12</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => updateCount('children', -1)}
                  disabled={guests.children <= 0}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MinusIcon className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium">{guests.children}</span>
                <button
                  onClick={() => updateCount('children', 1)}
                  disabled={!canAddGuest}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Infants */}
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Infants</div>
                <div className="text-sm text-gray-500">Under 2</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => updateCount('infants', -1)}
                  disabled={guests.infants <= 0}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MinusIcon className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium">{guests.infants}</span>
                <button
                  onClick={() => updateCount('infants', 1)}
                  disabled={guests.infants >= 5}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Max guests note */}
          <p className="text-sm text-gray-500 mt-4">
            This property allows a maximum of {maxGuests} guests (not including infants).
          </p>
        </div>
      )}
    </div>
  );
};

// Icons
const MinusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

export default GuestDetails;
